import {
  Children,
  forwardRef,
  isValidElement,
  useId,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useMemo,
} from "react";

import brandMarkUrl from "./assets/lonmax-mark.svg";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

type NativeSelectOptions = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
};

const parseNativeSelectOptions = (children: ReactNode): NativeSelectOptions[] => {
  const result: NativeSelectOptions[] = [];

  const walk = (nodes: ReactNode): void => {
    const iterable = Children.toArray(nodes);

    for (const node of iterable) {
      if (!isValidElement(node)) {
        continue;
      }

      const element = node as ReactElement<{
        value?: unknown;
        disabled?: boolean;
        children?: ReactNode;
      }>;

      if (typeof element.type === "string" && element.type.toLowerCase() === "option") {
        const fallback = element.props.children;
        const value = element.props.value ?? fallback;
        result.push({
          value: value === undefined || value === null ? "" : String(value),
          label: element.props.children,
          disabled: Boolean(element.props.disabled),
        });
        continue;
      }

      if (typeof element.type === "string" && element.type.toLowerCase() === "optgroup" && element.props.children) {
        walk(element.props.children);
      }
    }
  };

  walk(children);
  return result;
};

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "secondary" | "quiet" | "danger";
    size?: "small" | "medium";
    busy?: boolean;
  }
>(({ variant = "primary", size = "medium", busy, children, disabled, className = "", ...props }, ref) => (
  <button
    ref={ref}
    className={`as-button as-button--${variant} as-button--${size} ${className}`}
    disabled={disabled || busy}
    aria-busy={busy || undefined}
    {...props}
  >
    {busy ? <span className="as-spinner" aria-hidden="true" /> : null}
    {children}
  </button>
));
Button.displayName = "Button";

export const Card = ({ className = "", ...props }: HTMLAttributes<HTMLDivElement>) => (
  <section className={`as-card ${className}`} {...props} />
);

/** Shared brand mark: the LonMax AI "M" logo (vector master:
 *  assets/lonmax-mark.svg). PRODUCT.md §15.1 (updated 2026-08-30) adopts the
 *  official LonMax AI logo, replacing the interim lettermark.
 *  Decorative: surfaces pair it with visible brand text. */
export const BrandMark = ({ size = 30 }: { size?: number }) => (
  <img
    className="as-brand-mark"
    src={brandMarkUrl}
    alt=""
    aria-hidden="true"
    width={size}
    height={size}
  />
);

export const Badge = ({ tone = "neutral", children }: {
  tone?: "neutral" | "brand" | "success" | "warning" | "error";
  children: ReactNode;
}) => (
  <span className={`as-badge as-badge--${tone}`}>
    <span className="as-badge__dot" aria-hidden="true" />
    {children}
  </span>
);

export const PageHeader = ({ eyebrow, eyebrowIcon, title, description, actions }: {
  eyebrow?: ReactNode;
  eyebrowIcon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) => (
  <header className="as-page-header">
    <div>
      {eyebrow ? <div className="as-eyebrow">{eyebrowIcon}{eyebrow}</div> : null}
      <h1>{title}</h1>
      {description ? <p>{description}</p> : null}
    </div>
    {actions ? <div className="as-page-header__actions">{actions}</div> : null}
  </header>
);

export const PageSection = ({ title, description, action, children, className = "" }: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
}) => (
  <Card className={`as-page-section ${className}`}>
    <div className="as-page-section__head">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
    {children ? <div className="as-page-section__body">{children}</div> : null}
  </Card>
);

export const Row = ({ icon, title, detail, action }: {
  icon?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  action?: ReactNode;
}) => (
  <div className="as-row">
    {icon ? <span className="as-row__icon" aria-hidden="true">{icon}</span> : null}
    <div className="as-row__body">
      <strong>{title}</strong>
      {detail ? <span>{detail}</span> : null}
    </div>
    {action ? <div className="as-row__action">{action}</div> : null}
  </div>
);

export const Field = ({ label, hint, error, required, children }: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
}) => (
  <label className="as-field">
    <span className="as-field__label">
      {label}
      {required ? <span aria-hidden="true"> *</span> : null}
    </span>
    {children}
    {error ? <span className="as-field__error" role="alert">{error}</span> : hint ? <span className="as-field__hint">{hint}</span> : null}
  </label>
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className = "", ...props }, ref) => <input ref={ref} className={`as-input ${className}`} {...props} />,
);
Input.displayName = "Input";

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange"> & {
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  onValueChange?: (value: string) => void;
  placeholder?: string;
};

export const Select = ({
  className = "",
  value,
  onChange,
  onValueChange,
  children,
  disabled,
  required,
  name,
  ...props
}: SelectProps) => {
  const options = useMemo(() => parseNativeSelectOptions(children), [children]);
  const normalizedValue = value == null ? "" : String(value);

  const emitChange = (nextValue: string): void => {
    onValueChange?.(nextValue);
    if (!onChange) return;
    const event = {
      target: { value: nextValue },
      currentTarget: { value: nextValue },
    } as ChangeEvent<HTMLSelectElement>;
    onChange(event);
  };

  return (
    <span className={`as-select ${className}`}>
      {name ? <input type="hidden" name={name} value={normalizedValue} required={required} className="as-select__hidden-field" /> : null}
      <SelectPrimitive.Root value={normalizedValue} onValueChange={emitChange} disabled={disabled}>
        <SelectPrimitive.Trigger className="as-select__trigger" aria-invalid={props["aria-invalid"]}>
          {/* Locale-neutral fallback: callers pass a localized placeholder. */}
          <SelectPrimitive.Value placeholder={props.placeholder ?? "—"} />
          <SelectPrimitive.Icon className="as-select__icon" aria-hidden="true">▾</SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content className="as-select__content" sideOffset={6} align="start">
            <SelectPrimitive.Viewport className="as-select__viewport">
              {options.length === 0 ? (
                <SelectPrimitive.Item value="" disabled className="as-select__item" aria-disabled="true">
                  <SelectPrimitive.ItemText>—</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ) : (
                options.map((option, index) => (
                  <SelectPrimitive.Item
                    key={`${option.value}-${index}`}
                    value={option.value}
                    disabled={option.disabled}
                    className="as-select__item"
                  >
                    <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                    <SelectPrimitive.ItemIndicator className="as-select__item-indicator" aria-hidden="true">
                      ✓
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                ))
              )}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </span>
  );
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className = "", ...props }, ref) => <textarea ref={ref} className={`as-input as-textarea ${className}`} {...props} />,
);
Textarea.displayName = "Textarea";

export const StatePanel = ({ kind, title, description, action, requestId }: {
  kind: "loading" | "empty" | "error" | "forbidden";
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  requestId?: string;
}) => (
  <div className={`as-state as-state--${kind}`} role={kind === "error" ? "alert" : "status"}>
    {kind === "loading"
      ? <span className="as-spinner as-spinner--large" aria-hidden="true" />
      : <span className="as-state__mark" aria-hidden="true">{kind === "empty" ? "?" : "!"}</span>
    }
    <h2>{title}</h2>
    {description ? <p>{description}</p> : null}
    {requestId ? <RequestId value={requestId} /> : null}
    {action ? <div className="as-state__action">{action}</div> : null}
  </div>
);

export const MetricCard = ({ label, value, detail, trend, trendDir, sparkline, tone = "neutral" }: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  trend?: ReactNode;
  trendDir?: "up" | "down" | "flat";
  sparkline?: ReactNode;
  tone?: "neutral" | "brand" | "success" | "warning" | "error";
}) => (
  <Card className={`as-metric${tone === "neutral" ? "" : ` as-metric--${tone}`}`}>
    <span className="as-metric__label">{label}</span>
    <strong>{value}</strong>
    <div className="as-metric__foot">
      {detail ? <span>{detail}</span> : null}
      {trend ? <span className={`as-metric__trend${trendDir ? ` as-metric__trend--${trendDir}` : ""}`}>{trend}</span> : null}
    </div>
    {sparkline}
  </Card>
);

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: "start" | "end";
  className?: string;
}

export const DataTable = <T,>({ columns, rows, rowKey, caption, empty }: {
  columns: Array<Column<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  caption: string;
  empty?: ReactNode;
}) => {
  if (rows.length === 0 && empty) return <>{empty}</>;
  return (
    <div className="as-table-wrap" tabIndex={0} role="region" aria-label={caption}>
      <table className="as-table">
        <caption className="as-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => <th key={column.key} className={column.align === "end" ? "as-align-end" : ""}>{column.header}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td key={column.key} className={`${column.align === "end" ? "as-align-end" : ""} ${column.className ?? ""}`}>
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const RequestId = ({ value }: { value: string }) => (
  <code className="as-request-id" title={value}>{value}</code>
);

export const CodeBlock = ({ children, label, action }: { children: string; label?: ReactNode; action?: ReactNode }) => (
  <div className="as-code-block">
    {(label || action) ? <div className="as-code-block__header"><span>{label}</span>{action}</div> : null}
    <pre><code>{children}</code></pre>
  </div>
);

export const Segmented = ({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: Array<{ value: string; label: ReactNode }>;
  onChange: (value: string) => void;
}) => (
  <ToggleGroup.Root
    type="single"
    value={value}
    onValueChange={(nextValue) => nextValue !== undefined && onChange(nextValue)}
    className="as-segmented"
    aria-label={label}
  >
    {options.map((option) => (
      <ToggleGroup.Item
        key={option.value}
        value={option.value}
        className="as-segmented__item"
      >
        {option.label}
      </ToggleGroup.Item>
    ))}
  </ToggleGroup.Root>
);

export const Tabs = ({ className = "", ...props }: TabsPrimitive.TabsProps) => (
  <TabsPrimitive.Root className={`as-tabs ${className}`} {...props} />
);

export const TabsList = forwardRef<HTMLDivElement, TabsPrimitive.TabsListProps>(({ className = "", ...props }, ref) => (
  <TabsPrimitive.List ref={ref} className={`as-tabs__list ${className}`} {...props} />
));
TabsList.displayName = "TabsList";

export const TabsTrigger = forwardRef<HTMLButtonElement, TabsPrimitive.TabsTriggerProps>(({ className = "", ...props }, ref) => (
  <TabsPrimitive.Trigger ref={ref} className={`as-tabs__trigger ${className}`} {...props} />
));
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = forwardRef<HTMLDivElement, TabsPrimitive.TabsContentProps>(({ className = "", ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={`as-tabs__content ${className}`} {...props} />
));
TabsContent.displayName = "TabsContent";

export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  danger,
  busy,
  confirmationText,
  expectedText,
  onConfirmationText,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel: ReactNode;
  danger?: boolean;
  busy?: boolean;
  confirmationText?: string;
  expectedText?: string;
  onConfirmationText?: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const enabled = expectedText === undefined || confirmationText === expectedText;
  const titleId = useId();
  const descriptionId = useId();

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="as-dialog-overlay" />
        <DialogPrimitive.Content className="as-dialog as-dialog--confirm" aria-labelledby={titleId} aria-describedby={descriptionId}>
          <DialogPrimitive.Title id={titleId} className="as-dialog__title">{title}</DialogPrimitive.Title>
          <DialogPrimitive.Description id={descriptionId} className="as-dialog__description">
            {description}
          </DialogPrimitive.Description>
          {expectedText !== undefined ? (
            <Field label={<span>Please enter <strong>{expectedText}</strong></span>}>
              <Input
                autoFocus
                value={confirmationText}
                onChange={(event) => onConfirmationText?.(event.target.value)}
              />
            </Field>
          ) : null}
          <div className="as-dialog__actions">
            <DialogPrimitive.Close asChild>
              <Button variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
            </DialogPrimitive.Close>
            <Button variant={danger ? "danger" : "primary"} disabled={!enabled} busy={busy} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxPrimitive.CheckboxProps>(({ className = "", ...props }, ref) => (
  <CheckboxPrimitive.Root ref={ref} className={`as-checkbox-root ${className}`} {...props}>
    <CheckboxPrimitive.Indicator className="as-checkbox-indicator" forceMount>
      <span aria-hidden="true">✓</span>
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = "Checkbox";

export const Switch = forwardRef<HTMLButtonElement, SwitchPrimitive.SwitchProps>(({ className = "", ...props }, ref) => (
  <SwitchPrimitive.Root ref={ref} className={`as-switch ${className}`} {...props}>
    <SwitchPrimitive.Thumb className="as-switch-thumb" />
  </SwitchPrimitive.Root>
));
Switch.displayName = "Switch";

export const RadioGroup = ({ className = "", ...props }: RadioGroupPrimitive.RadioGroupProps) => (
  <RadioGroupPrimitive.Root className={`as-radio-group ${className}`} {...props} />
);

export const RadioItem = forwardRef<HTMLButtonElement, RadioGroupPrimitive.RadioGroupItemProps>(({ className = "", ...props }, ref) => (
  <RadioGroupPrimitive.Item ref={ref} className={`as-radio-item ${className}`} {...props}>
    <RadioGroupPrimitive.Indicator className="as-radio-indicator">
      <span aria-hidden="true">●</span>
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioItem.displayName = "RadioItem";

export const Popover = ({ ...props }: PopoverPrimitive.PopoverProps) => <PopoverPrimitive.Root {...props} />;

export const PopoverTrigger = forwardRef<HTMLButtonElement, PopoverPrimitive.PopoverTriggerProps>(({ className = "", ...props }, ref) => (
  <PopoverPrimitive.Trigger ref={ref} className={`as-popover-trigger ${className}`} {...props} />
));
PopoverTrigger.displayName = "PopoverTrigger";

export const PopoverContent = forwardRef<HTMLDivElement, PopoverPrimitive.PopoverContentProps>(({
  className = "",
  sideOffset = 8,
  ...props
}, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={`as-popover-content ${className}`}
      {...props}
    >
      {props.children}
      <PopoverPrimitive.Arrow className="as-popover-arrow" />
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = "PopoverContent";

export const PopoverClose = forwardRef<HTMLButtonElement, PopoverPrimitive.PopoverCloseProps>(({ className = "", ...props }, ref) => (
  <PopoverPrimitive.Close ref={ref} className={`as-popover-close ${className}`} {...props} />
));
PopoverClose.displayName = "PopoverClose";

export const Tooltip = ({ children, content, side = "top", align = "center", delayDuration = 300 }: {
  children: ReactElement;
  content: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  delayDuration?: number;
}) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={0}>
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content className="as-tooltip" side={side} align={align} sideOffset={6}>
          {content}
          <TooltipPrimitive.Arrow className="as-tooltip__arrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  </TooltipPrimitive.Provider>
);

export const formatNumber = (value: number, locale = "zh-CN", options?: Intl.NumberFormatOptions): string =>
  new Intl.NumberFormat(locale, options).format(value);

export const formatDate = (value: string | Date | undefined, locale = "zh-CN", options?: Intl.DateTimeFormatOptions): string =>
  value ? new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short", ...options }).format(new Date(value)) : "-";

export const formatMoney = (minor: number, currency: string, locale = "zh-CN"): string =>
  new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / 100);

export const toneForStatus = (status: string): "neutral" | "brand" | "success" | "warning" | "error" => {
  if (["active", "healthy", "succeeded", "completed", "paid", "settled", "published"].includes(status)) return "success";
  if (["pending", "processing", "reconciling", "degraded", "testing"].includes(status)) return "warning";
  if (["failed", "error", "suspended", "disabled", "revoked", "closed"].includes(status)) return "error";
  return "neutral";
};
